import { Component, computed, ElementRef, input, model, ModelSignal, output, signal, viewChild } from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';

export type BaasNativeDatePickerMode = 'date' | 'dateTime' | 'dateTimeSeconds';

/**
 * @experimental This component is experimental and currently under review.
 *
 * Native HTML date/time picker for signal forms.
 *
 * Use `mode` to control which fields are shown:
 *  - `'date'` (default) — date only (`<input type="date">`)
 *  - `'dateTime'` — date + time (`<input type="datetime-local">`)
 *  - `'dateTimeSeconds'` — date + time + seconds
 *
 * Compared to `BaasDateTimePicker` (owl-based), the following features are NOT supported:
 *  - `range` / `rangeSelectMode` — native inputs have no range-selection UI
 *  - `startAt` — the native calendar popup cannot be pre-positioned
 *  - `dateTimeFilter` — arbitrary date filtering is unsupported; use `minDate`/`maxDate` instead
 *
 * For time-only input, use `BaasNativeTimePicker` instead.
 */
@Component({
    selector: 'baas-native-date-time-picker',
    templateUrl: './baas-native-date-time-picker.html',
    host: { class: 'baas-native-date-time-picker baas-native-temporal-picker' }
})
export class BaasNativeDateTimePicker implements FormValueControl<Date | null | undefined> {
    readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input');
    // NOTE typing needed for d.ts generation, otherwise it becomes of type Date
    readonly value: ModelSignal<Date | null | undefined> = model<Date | null | undefined>(undefined);
    /** Id used to associate with label for. */
    readonly labelForId = input<string>();
    /** Controls which fields are shown. `'date'` = date only, `'dateTime'` = date + time, `'dateTimeSeconds'` = date + time + seconds. For time-only, use `BaasNativeTimePicker`. */
    readonly mode = input<BaasNativeDatePickerMode>('date');
    /** Min selectable date/time. */
    readonly minDate = input<Date | undefined>();
    /** Max selectable date/time. */
    readonly maxDate = input<Date | undefined>();
    readonly readonly = input(false);
    readonly required = input<boolean>();
    readonly disabled = input<boolean>();
    /** Occurs on input blur. */
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly blur = output();
    /** Occurs when the value changes. */
    readonly changed = output<Date | null>();

    protected readonly inputType = computed(() => (this.mode() === 'date' ? 'date' : 'datetime-local'));

    protected readonly step = computed(() => {
        switch (this.mode()) {
            case 'dateTimeSeconds':
                return 1;
            case 'dateTime':
                return 60;
            default:
                return undefined;
        }
    });

    protected readonly draftValue = signal<string | null>(null);

    protected readonly inputValue = computed(() => this.draftValue() ?? this.#format(this.value()));

    protected readonly minStr = computed(() => this.#format(this.minDate()));

    protected readonly maxStr = computed(() => this.#format(this.maxDate()));

    focus(): void {
        this.input()?.nativeElement?.focus();
    }

    protected onInput(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this.draftValue.set(raw);
        if (raw) {
            const parsed = this.#parse(raw);
            if (parsed) this.value.set(parsed);
        }
    }

    protected onFocus(event: FocusEvent): void {
        this.draftValue.set((event.target as HTMLInputElement).value);
    }

    protected onInputChange(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this.draftValue.set(raw);
        const parsed = raw ? this.#parse(raw) : null;
        this.value.set(parsed);
        this.changed.emit(parsed);
    }

    protected onBlur(): void {
        this.draftValue.set(null);
        this.blur.emit();
    }

    // -------------------------------------------------------------------------
    // Formatting / parsing helpers (all local-time based, no UTC shifts)
    // -------------------------------------------------------------------------

    #format(date: Date | null | undefined): string {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';

        const pad = (n: number, len = 2) => n.toString().padStart(len, '0');
        const year = pad(date.getFullYear(), 4);
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());

        if (this.mode() !== 'date') {
            const hh = pad(date.getHours());
            const mm = pad(date.getMinutes());
            const ss = pad(date.getSeconds());
            return this.mode() === 'dateTimeSeconds' ? `${year}-${month}-${day}T${hh}:${mm}:${ss}` : `${year}-${month}-${day}T${hh}:${mm}`;
        }

        return `${year}-${month}-${day}`;
    }

    #parse(raw: string): Date | null {
        if (!raw) return null;

        if (this.mode() !== 'date') {
            // "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss" — no timezone suffix → local time
            const [datePart, timePart] = raw.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);
            const d = new Date(year, month - 1, day, hours, minutes, seconds);
            return isNaN(d.getTime()) ? null : d;
        }

        // "YYYY-MM-DD" — parse as local time, NOT as UTC
        const [year, month, day] = raw.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        return isNaN(d.getTime()) ? null : d;
    }
}
